import { useMutation, useQueryClient } from '@tanstack/react-query';
import { submitInvoice } from '../services/agreements';
import { isBalanceRejection, readInvoiceRejection } from '../services/invoiceRejection';
import { agreementKeys } from './agreementKeys';
import { useCashPositionSettle } from './useCashPositionSettle';
import type { AgreementInvoice, SubmitInvoicePayload } from '../types/agreements.types';

/** On success invalidates the invoices, sold-items, cash-position and financials keys, then polls
 *  the cash position until the claim lands (`settling`); on failure only a balance rejection invalidates it. */
export function useSubmitInvoiceMutation(agreementId: string) {
    const queryClient = useQueryClient();
    const settle = useCashPositionSettle(agreementId);

    const mutation = useMutation<AgreementInvoice, unknown, SubmitInvoicePayload, number | null>({
        mutationFn: (payload) => submitInvoice(agreementId, payload),
        onMutate: () => settle.readBaseline(),
        onError: (error) => {
            if (isBalanceRejection(readInvoiceRejection(error))) {
                return queryClient.invalidateQueries({ queryKey: agreementKeys.cashPosition(agreementId) });
            }
        },
        onSuccess: async (_invoice, _payload, baseline) => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: agreementKeys.invoices(agreementId) }),
                queryClient.invalidateQueries({ queryKey: agreementKeys.soldItems(agreementId) }),
                queryClient.invalidateQueries({ queryKey: agreementKeys.cashPosition(agreementId) }),
                queryClient.invalidateQueries({ queryKey: agreementKeys.financials(agreementId) }),
            ]);
            settle.settleFrom(baseline ?? null);
        },
    });
    return { ...mutation, settling: settle.settling };
}
